import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  QrCodeIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { analyticsService, deviceService, getApiUrl } from '../services/api.ts';
import { format, subDays } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ro as roDF } from 'date-fns/locale';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

// Color palettes for charts
const SCORE_CATEGORY_COLORS = {
  excellent: '#10b981', // green
  good: '#84cc16',      // light green
  fair: '#eab308',      // yellow
  poor: '#f97316',      // orange
  very_poor: '#ef4444'  // red
};

// Location colors - distinct from score categories
const LOCATION_COLORS = [
  '#3B82F6', // blue
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#14B8A6', // teal
  '#F59E0B', // amber
  '#6366F1', // indigo
];

function Dashboard() {
  const { t, i18n } = useTranslation();
  const dfLocale = i18n.language?.startsWith('en') ? enUS : roDF;

  // Reports state
  const [reportData, setReportData] = useState<any>(null);
  const [scoreDistribution, setScoreDistribution] = useState<any>(null);
  const [qrAccessStats, setQrAccessStats] = useState<any>(null);
  const [reportDays, setReportDays] = useState<number>(7);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Devices state
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  // Fetch reports
  const fetchReports = React.useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setReportsLoading(true);
      }
      setReportsError(null);
      const endDate = new Date();
      const startDate = subDays(endDate, reportDays);
      
      // Fetch all data in parallel with timeout
      const fetchWithTimeout = (promise: Promise<any>, timeout = 5000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error(i18n.t('errors.requestTimeout'))), timeout)
          )
        ]);
      };

      const [summaryData, distributionData, qrStats] = await Promise.all([
        fetchWithTimeout(
          analyticsService.getSummaryReport(
            startDate.toISOString().split('T')[0],
            endDate.toISOString().split('T')[0]
          )
        ),
        fetchWithTimeout(
          analyticsService.getScoreDistribution(
            startDate.toISOString().split('T')[0],
            endDate.toISOString().split('T')[0]
          )
        ),
        fetchWithTimeout(
          fetch(getApiUrl(`/api/reports/qr-access-stats?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`))
            .then(res => res.json())
        ).catch(err => {
          console.error('Failed to fetch QR stats:', err);
          return null;
        })
      ]);
      
      setReportData(summaryData);
      setScoreDistribution(distributionData);
      setQrAccessStats(qrStats);
    } catch (err: any) {
      console.error('Failed to fetch reports:', err);
      setReportsError(err?.response?.data?.detail || err?.message || i18n.t('errors.loadReportsData'));
    } finally {
      setReportsLoading(false);
    }
  }, [reportDays, i18n]);

  // Fetch devices
  const fetchDevices = React.useCallback(async () => {
    try {
      setDevicesLoading(true);
      const data = await deviceService.getDevices(
        undefined,
        undefined,
        true,
        undefined
      );
      setDevices(data);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchDevices();
    setLastRefresh(new Date());
  }, [fetchReports, fetchDevices]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchReports(false);
      fetchDevices();
      setLastRefresh(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchReports, fetchDevices]);

  // Memoize chart data
  const chartData = useMemo(() => {
    if (!reportData?.trends) return [];
    return reportData.trends.map((trend: any) => ({
      date: format(new Date(trend.date), 'MMM dd', { locale: dfLocale }),
      fullDate: trend.date,
      sessions: trend.analyses_count || 0,
      avgScore: trend.average_score
    }));
  }, [reportData?.trends, dfLocale]);

  const scoreDistributionData = useMemo(() => {
    if (!scoreDistribution?.categories) return [];
    return Object.entries(scoreDistribution.categories).map(([name, value]) => ({
      name: t(`dashboard.scoreCategory.${name}` as never),
      value: value as number,
      color: SCORE_CATEGORY_COLORS[name as keyof typeof SCORE_CATEGORY_COLORS] || '#64748b'
    }));
  }, [scoreDistribution?.categories, t]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-regina-gray-800 sm:text-2xl">
          {t('dashboard.title')}
        </h1>
        <p className="mt-1 text-sm text-regina-gray-600">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Reports Error Display */}
      {reportsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-regina-error mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-red-800 font-medium">{t('dashboard.reportsLoadErrorTitle')}</div>
              <div className="text-red-700 text-sm mt-1">{reportsError}</div>
            </div>
          </div>
        </div>
      )}

      {/* Device Stats Cards */}
      {!devicesLoading && devices.length > 0 && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Total Devices */}
          <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-regina-hover sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-50 rounded-lg">
                <ComputerDesktopIcon className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-3xl font-bold text-regina-gray-800">{devices.length}</span>
            </div>
            <h3 className="text-sm font-medium text-regina-gray-700">{t('dashboard.totalDevices')}</h3>
            <p className="text-xs text-regina-gray-600 mt-1">
              {t('dashboard.onlineOnlineOffline', {
                online: devices.filter(d => d.is_online).length,
                offline: devices.filter(d => !d.is_online).length,
              })}
            </p>
          </div>

          {/* Online Devices */}
          <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-regina-hover sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-regina-success" />
              </div>
              <span className="text-3xl font-bold text-regina-gray-800">{devices.filter(d => d.is_online).length}</span>
            </div>
            <h3 className="text-sm font-medium text-regina-gray-700">{t('dashboard.onlineCardTitle')}</h3>
            <p className="text-xs text-regina-gray-600 mt-1">
              {t('dashboard.onlineCardHint')}
            </p>
          </div>

          {/* Offline Devices */}
          <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-regina-hover sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-red-50 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-regina-error" />
              </div>
              <span className="text-3xl font-bold text-regina-gray-800">{devices.filter(d => !d.is_online).length}</span>
            </div>
            <h3 className="text-sm font-medium text-regina-gray-700">{t('dashboard.offlineCardTitle')}</h3>
            <p className="text-xs text-regina-gray-600 mt-1">
              {t('dashboard.offlineCardHint')}
            </p>
          </div>
        </div>
      )}

      {reportData && (
        <>
          {/* QR Code Access Statistics */}
          {qrAccessStats && qrAccessStats.summary && (
            <div className="bg-white rounded-lg border border-regina-gray-100 p-4 shadow-sm sm:p-6">
              <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-regina-gray-800 sm:mb-6 sm:text-lg">
                <QrCodeIcon className="h-5 w-5 shrink-0 text-regina-red sm:h-6 sm:w-6" />
                <span className="min-w-0 leading-snug">{t('dashboard.qrStatsTitle')}</span>
              </h2>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
                <div className="bg-regina-gray-50 rounded-lg p-4 border border-regina-gray-100">
                  <p className="text-regina-gray-600 text-sm mb-1">{t('dashboard.qrGenerated')}</p>
                  <p className="text-2xl font-bold text-regina-gray-800">{qrAccessStats.summary.total_qr_generated}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-green-700 text-sm mb-1">{t('dashboard.reportsAccessed')}</p>
                  <p className="text-2xl font-bold text-regina-success">{qrAccessStats.summary.total_accessed}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-blue-700 text-sm mb-1">{t('dashboard.accessRate')}</p>
                  <p className="text-2xl font-bold text-blue-600">{qrAccessStats.summary.access_rate_percentage}%</p>
                </div>
              </div>
            </div>
          )}

          {/* Posture Analysis Trends */}
          <div className="bg-white rounded-lg border border-regina-gray-100 p-4 shadow-sm sm:p-6">
            <div className="mb-4 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-base font-semibold text-regina-gray-800 sm:text-lg">
                {t('dashboard.trendsTitle')}
              </h2>

              {/* Time Range Selector */}
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:space-x-2 sm:gap-0">
                <span className="text-sm text-regina-gray-600">{t('dashboard.timePeriod')}</span>
                <select
                  value={reportDays}
                  onChange={(e) => setReportDays(Number(e.target.value))}
                  className="w-full min-w-0 rounded-lg border border-regina-gray-200 bg-white px-3 py-2 text-sm text-regina-gray-800 focus:outline-none focus:ring-2 focus:ring-regina-red sm:w-auto sm:py-1.5"
                >
                  <option value={7}>{t('dashboard.lastDays', { count: 7 })}</option>
                  <option value={14}>{t('dashboard.lastDays', { count: 14 })}</option>
                  <option value={30}>{t('dashboard.lastDays', { count: 30 })}</option>
                  <option value={60}>{t('dashboard.lastDays', { count: 60 })}</option>
                  <option value={90}>{t('dashboard.lastDays', { count: 90 })}</option>
                </select>
              </div>
            </div>

            {chartData && chartData.length > 0 ? (
              <div className="h-[220px] w-full min-h-[200px] sm:h-[280px] lg:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 8, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#94a3b8"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#94a3b8"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#f1f5f9'
                    }}
                    itemStyle={{
                      color: '#f1f5f9'
                    }}
                    labelStyle={{
                      color: '#e2e8f0',
                      fontWeight: 'bold'
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="line"
                  />
                  <Line
                    type="monotone"
                    dataKey="sessions"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', r: 4 }}
                    activeDot={{ r: 6 }}
                    name={t('dashboard.chartSessions')}
                  />
                </LineChart>
              </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-48 items-center justify-center text-regina-gray-600 sm:h-64">
                {t('dashboard.noTrendData')}
              </div>
            )}
          </div>

          {/* Score Distribution Chart */}
          {scoreDistribution && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart - Score Categories */}
              <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm sm:p-6">
                <h2 className="mb-4 text-base font-semibold text-regina-gray-800 sm:text-lg">
                  {t('dashboard.scoreDistributionTitle')}
                </h2>
                {scoreDistributionData && scoreDistributionData.length > 0 ? (
                  <div className="flex flex-col items-start gap-6 pt-4 pb-8 sm:gap-8 sm:pt-8 sm:pb-12 lg:flex-row">
                    {/* Legend / category cards first (left on desktop, top on mobile) */}
                    {scoreDistribution?.categories && (
                      <div className="w-full lg:max-w-sm space-y-4 order-1">
                        <div className="flex items-center justify-between gap-4 bg-regina-gray-50 rounded-lg p-4 border border-regina-gray-100">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 rounded shrink-0" style={{ backgroundColor: SCORE_CATEGORY_COLORS.excellent }} />
                            <span className="text-base text-regina-gray-700 font-medium">{t('dashboard.scoreCategory.excellent')}</span>
                          </div>
                          <span className="text-xl font-bold text-regina-gray-800 shrink-0">{scoreDistribution.categories.excellent}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 bg-regina-gray-50 rounded-lg p-4 border border-regina-gray-100">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 rounded shrink-0" style={{ backgroundColor: SCORE_CATEGORY_COLORS.good }} />
                            <span className="text-base text-regina-gray-700 font-medium">{t('dashboard.scoreCategory.good')}</span>
                          </div>
                          <span className="text-xl font-bold text-regina-gray-800 shrink-0">{scoreDistribution.categories.good}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 bg-regina-gray-50 rounded-lg p-4 border border-regina-gray-100">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 rounded shrink-0" style={{ backgroundColor: SCORE_CATEGORY_COLORS.fair }} />
                            <span className="text-base text-regina-gray-700 font-medium">{t('dashboard.scoreCategory.fair')}</span>
                          </div>
                          <span className="text-xl font-bold text-regina-gray-800 shrink-0">{scoreDistribution.categories.fair}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 bg-regina-gray-50 rounded-lg p-4 border border-regina-gray-100">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 rounded shrink-0" style={{ backgroundColor: SCORE_CATEGORY_COLORS.poor }} />
                            <span className="text-base text-regina-gray-700 font-medium">{t('dashboard.scoreCategory.poor')}</span>
                          </div>
                          <span className="text-xl font-bold text-regina-gray-800 shrink-0">{scoreDistribution.categories.poor}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 bg-regina-gray-50 rounded-lg p-4 border border-regina-gray-100">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 rounded shrink-0" style={{ backgroundColor: SCORE_CATEGORY_COLORS.very_poor }} />
                            <span className="text-base text-regina-gray-700 font-medium">{t('dashboard.scoreCategory.very_poor')}</span>
                          </div>
                          <span className="text-xl font-bold text-regina-gray-800 shrink-0">{scoreDistribution.categories.very_poor}</span>
                        </div>
                      </div>
                    )}
                    {/* Pie Chart - right side on desktop, below cards on mobile */}
                    <div className="order-2 flex w-full max-w-[280px] shrink-0 justify-center justify-self-center overflow-x-auto sm:max-w-none">
                      <PieChart width={260} height={260} className="sm:h-[280px] sm:w-[280px]">
                        <Pie
                          data={scoreDistributionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={false}
                          outerRadius={100}
                          innerRadius={60}
                          dataKey="value"
                        >
                          {scoreDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1e293b',
                            border: '1px solid #334155',
                            borderRadius: '8px',
                            color: '#f1f5f9'
                          }}
                          itemStyle={{
                            color: '#f1f5f9'
                          }}
                          labelStyle={{
                            color: '#e2e8f0',
                            fontWeight: 'bold'
                          }}
                        />
                      </PieChart>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-regina-gray-600">
                    {t('dashboard.noDataPeriod')}
                  </div>
                )}
              </div>

              {/* Bar Chart - Sessions by Location */}
              <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm sm:p-6">
                <h2 className="mb-4 text-base font-semibold text-regina-gray-800 sm:text-lg">
                  {t('dashboard.sessionsByLocation')}
                </h2>
                {reportData && reportData.location_breakdown && reportData.location_breakdown.length > 0 ? (
                  <div className="space-y-4">
                    {/* Bar Chart */}
                    <div className="h-[200px] w-full min-h-[180px] sm:h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={reportData.location_breakdown.map((loc: any, index: number) => ({
                          name: loc.location_name.length > 15 ? loc.location_name.substring(0, 15) + '...' : loc.location_name,
                          fullName: loc.location_name,
                          sessions: loc.total_sessions || 0,
                          color: LOCATION_COLORS[index % LOCATION_COLORS.length]
                        }))}
                        margin={{ top: 5, right: 4, left: 0, bottom: 24 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="name" 
                          stroke="#94a3b8"
                          style={{ fontSize: '11px' }}
                          tick={{ fill: '#64748b' }}
                        />
                        <YAxis 
                          stroke="#94a3b8"
                          style={{ fontSize: '11px' }}
                          tick={{ fill: '#64748b' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1e293b',
                            border: '1px solid #334155',
                            borderRadius: '8px',
                            color: '#f1f5f9'
                          }}
                          itemStyle={{
                            color: '#f1f5f9'
                          }}
                          labelStyle={{
                            color: '#e2e8f0',
                            fontWeight: 'bold'
                          }}
                          formatter={(value: any, _name: any, props: any) => {
                            return [t('dashboard.barTooltipSessions', { count: value }), props.payload.fullName];
                          }}
                        />
                        <Bar 
                          dataKey="sessions" 
                          fill="#3B82F6"
                          radius={[8, 8, 0, 0]}
                        >
                          {reportData.location_breakdown.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={LOCATION_COLORS[index % LOCATION_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    </div>
                    
                    {/* Location Stats Summary */}
                    <div className="space-y-2">
                      {reportData.location_breakdown.map((loc: any, index: number) => (
                        <div key={index} className="flex items-center justify-between bg-regina-gray-50 rounded-lg p-3 border border-regina-gray-100 hover:border-regina-red transition-colors">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: LOCATION_COLORS[index % LOCATION_COLORS.length] }}
                            ></div>
                            <span className="text-sm text-regina-gray-700 font-medium truncate">{loc.location_name}</span>
                          </div>
                          <div className="text-right ml-3">
                            <div className="text-lg font-bold text-regina-gray-800">{loc.total_sessions}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-regina-gray-600">
                    {t('dashboard.noDataPeriod')}
                  </div>
                )}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
}

export default Dashboard;

